import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useHistory } from 'react-router-dom';
import styles from './styles/styles.module.scss';
import GiftCard from '../GiftCard';
import GiftCardRadioButtons from '../GiftCardRadioButtons';
import ToggleRadioButtons from '../ToggleRadioButtons';
import OutlinedTextField from '../../forms/textFields/OutlinedTextField';
import FormButton from '../../forms/FormButton';
import { PageContext } from '../../shared/Page/Page';
import { GiftFlowContext, UPDATE_GIFT_CUSTOMIZE_FORM, UPDATE_SERVER_ERRORS } from '../GiftFlow/GiftFlow';
import DatePicker from '../../shared/DatePicker';
import { StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import GiftTray from '../GiftTray';
import { validateGiftForm, hasFormErrors } from '../../../utils/form_validaters/gift_flow_helper';
import { pushHistory } from '../../../utils/client_side_routing_helpers';
import { isToday } from '../../../utils/time_helpers';
import { DESKTOP_BREAKPOINT } from '../../../utils/constants/breakpoints';

const {
  strings: {
    GIFT_FLOW: {
      GIFT_CUSTOMIZE_FORM: {
        EMAIL,
        PRINT,
        CUSTOMIZE_YOUR_GIFT,
        HOW_DO_YOU_WANT_TO_SEND,
        TO,
        FROM,
        NAME,
        MESSAGE,
        SEND_DATE,
        NEXT,
        FIVE_HUNDRED_CHARACTERS_MAX,
        SEND_TODAY,
        SEND_AT_A_LATER_DATE
      }
    }
  }
} = StringConstantsMixin;

const printEmailOptions = [
  { label: EMAIL, value: 'email', name: 'sendMethod' },
  { label: PRINT, value: 'print', name: 'sendMethod' }
];

const CustomizeGiftForm = () => {
  // contexts
  const {
    giftFlowState: { giftCustomizeParams, serverErrors, valentinesDayVariant },
    dispatchGiftFlow
  } = React.useContext(GiftFlowContext);

  const {
    globalState: {
      context: { currentUser, languageRegionCode },
      windowWidth
    },
    modalRef
  } = React.useContext(PageContext);
  const history = useHistory();

  // useStates
  const [formParams, updateFormParams] = useState({ ...giftCustomizeParams });
  const [hasSubmitted, updateHasSubmitted] = useState(false);
  const [renderBottomTray, toggleRenderBottomTray] = useState(false);

  const updateForm = e => {
    const { name, value } = e.currentTarget;
    updateFormParams(prevState => ({ ...prevState, [name]: value }));
  };

  const navigateTo = path => {
    pushHistory(path, history, languageRegionCode);
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
  };

  const handleDateChange = sendDate => {
    updateFormParams(prevState => ({ ...prevState, sendDate }));
  };

  const checkErrorMessage = (name, value, validate) => {
    if (!validate) {
      return '';
    }

    return validateGiftForm(name, value);
  };

  const checkServerError = value => {
    if (serverErrors.length === 0) {
      return '';
    }

    const error = serverErrors.find(err => {
      return err.target === value;
    });

    return error ? error.message : '';
  };

  const handleFormSubmit = e => {
    e.preventDefault();

    if (!hasSubmitted) {
      updateHasSubmitted(true);
    }

    if (hasFormErrors(formParams)) {
      return;
    }

    if (serverErrors.length > 0) {
      dispatchGiftFlow({ type: UPDATE_SERVER_ERRORS, payload: [] });
    }

    dispatchGiftFlow({
      type: UPDATE_GIFT_CUSTOMIZE_FORM,
      payload: formParams
    });

    currentUser ? navigateTo('/gift/payment') : toggleRenderBottomTray(true);
  };

  const messageFieldError = () => {
    if (formParams.message.trim().length > 500) {
      return FIVE_HUNDRED_CHARACTERS_MAX;
    }

    return '';
  };

  const sendDateHelperText = useMemo(() => {
    return isToday(formParams.sendDate) ? SEND_TODAY : SEND_AT_A_LATER_DATE;
  }, [formParams.sendDate]);

  return (
    <div className={styles.container}>
      <div className={styles.cardColumn}>
        <div className={styles.fixedContainer}>
          <GiftCard backgroundId={formParams.backgroundId} />
        </div>
      </div>
      <form className={styles.formColumn} onSubmit={handleFormSubmit}>
        <h2 className={styles.formHeader}>{CUSTOMIZE_YOUR_GIFT}</h2>
        <div className={styles.formSection}>
          {windowWidth && windowWidth < DESKTOP_BREAKPOINT && (
            <div className={styles.mobileGiftCardContainer}>
              <GiftCard backgroundId={formParams.backgroundId} />
            </div>
          )}
          <GiftCardRadioButtons
            onChange={updateForm}
            selectedVariant={formParams.backgroundId}
            languageRegionCode={languageRegionCode}
            valentinesDayVariant={valentinesDayVariant}
          />
        </div>
        <div className={styles.formSection}>
          <div className={styles.label}>{HOW_DO_YOU_WANT_TO_SEND}</div>
          <ToggleRadioButtons options={printEmailOptions} handleOnChange={updateForm} activeOption={formParams.sendMethod} />
        </div>
        <div className={styles.formSection}>
          <div className={styles.label}>{TO}:</div>
          <div className={styles.inputContainer}>
            <OutlinedTextField
              name="recipientName"
              errorMessage={checkErrorMessage('recipientName', formParams.recipientName, hasSubmitted)}
              value={formParams.recipientName}
              label={NAME}
              changeHandler={updateForm}
            />
          </div>
          {formParams.sendMethod === printEmailOptions[0].value && (
            <div className={styles.inputContainer}>
              <OutlinedTextField
                name="recipientEmail"
                errorMessage={
                  checkErrorMessage('recipientEmail', formParams.recipientEmail, hasSubmitted) || checkServerError(formParams.recipientEmail)
                }
                value={formParams.recipientEmail}
                label={EMAIL}
                changeHandler={updateForm}
              />
            </div>
          )}
          <div className={styles.inputContainer}>
            <OutlinedTextField
              name="message"
              errorMessage={messageFieldError()}
              value={formParams.message}
              label={MESSAGE}
              changeHandler={updateForm}
              rows={9}
              multiline
            />
          </div>
        </div>
        <div className={styles.formSection}>
          <div className={styles.label}>{FROM}:</div>
          <div className={styles.inputContainer}>
            <OutlinedTextField
              name="senderName"
              errorMessage={checkErrorMessage('senderName', formParams.senderName, hasSubmitted)}
              value={formParams.senderName}
              label={NAME}
              changeHandler={updateForm}
            />
          </div>
          <div className={styles.inputContainer}>
            <OutlinedTextField
              name="senderEmail"
              errorMessage={checkErrorMessage('senderEmail', formParams.senderEmail, hasSubmitted) || checkServerError(formParams.senderEmail)}
              value={formParams.senderEmail}
              label={EMAIL}
              changeHandler={updateForm}
            />
          </div>
        </div>
        {formParams.sendMethod === printEmailOptions[0].value && (
          <div className={styles.formSection}>
            <div className={styles.label}>
              {SEND_DATE}: <span className={styles.sendDateHelperText}>{sendDateHelperText}.</span>
            </div>
            <DatePicker
              fullWidth
              disablePast
              disableFuture={false}
              date={formParams.sendDate}
              onChange={handleDateChange}
              locale={languageRegionCode}
            />
          </div>
        )}
        <div className={styles.formSection}>
          <FormButton value={NEXT} />
        </div>
      </form>
      {renderBottomTray && ReactDOM.createPortal(<GiftTray closeTray={() => toggleRenderBottomTray(false)} navigateTo={navigateTo} />, modalRef)}
    </div>
  );
};

export default CustomizeGiftForm;
